import type { ReactElement } from 'react'
import styles from './SuggesterAlert.module.css'

/**
 * A single alert action button.
 */
export interface AlertAction {
  /** Button label. */
  readonly label: string
  /** Invoked when the button is activated. */
  readonly onPress: () => void
  /** `true` for the emphasised (semibold) action. */
  readonly emphasised?: boolean
}

/**
 * Props for {@link SuggesterAlert}.
 */
export interface SuggesterAlertProps {
  /** Alert title. */
  readonly title: string
  /** Alert body copy. */
  readonly body: string
  /** Action buttons. */
  readonly actions: readonly AlertAction[]
  /**
   * Layout of the action buttons:
   * - `'row'` — two side-by-side buttons (the "Location Access Required"
   *   alert uses this).
   * - `'stacked'` — full-width buttons stacked vertically (generic
   *   support; not currently used by Suggester).
   */
  readonly layout: 'stacked' | 'row'
  /** Called when the dim backdrop is tapped. */
  readonly onBackdrop: () => void
  /**
   * Accessible label of the dim-backdrop dismiss control.
   * @defaultValue `'Dismiss'`
   */
  readonly dismissLabel?: string
}

/**
 * Modal alert rendered over the Suggester. Suggester uses it for the
 * app-drawn "Location Access Required" alert (spec §14) — shown when
 * geolocation permission is already denied. A dim backdrop plus a
 * centred 270px card.
 *
 * NOTE: the system permission *prompt* is NOT this component — it is
 * the browser's own native dialog, triggered by `navigator.geolocation`.
 *
 * Figma node 6292:15876 ("Location Access Required"). The Figma
 * "permission alert" frame (6291:15876) is reference-only and is not
 * implemented — the real permission prompt is OS-native.
 */
export function SuggesterAlert({
  title,
  body,
  actions,
  layout,
  onBackdrop,
  dismissLabel = 'Dismiss',
}: SuggesterAlertProps): ReactElement {
  return (
    <div className={styles.overlay}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label={dismissLabel}
        onClick={onBackdrop}
      />
      <div className={styles.card} role="alertdialog" aria-label={title}>
        <div className={styles.text}>
          <p className={styles.title}>{title}</p>
          <p className={styles.body}>{body}</p>
        </div>
        <div className={layout === 'row' ? styles.buttonsRow : styles.buttonsStacked}>
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={action.emphasised ? styles.buttonEmphasised : styles.button}
              onClick={action.onPress}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
